/**
 * Code 128 encoding (SCOPE §7).
 *
 * Labels have to scan. A barcode drawn as `*TEXT*` in a monospace font is a
 * picture of a barcode, and at the receiving bench that is indistinguishable
 * from a broken label — the operator finds out one item at a time.
 *
 * Code 128 subset B covers the full printable ASCII range, which is what a
 * numeric SKU uses as well as any numeric UPC. Subset C would pack digit pairs
 * more tightly; one code path that always works is worth more, and
 * `code128ModuleDots` below says outright when a code will not fit rather than
 * leaving density to be discovered at the register.
 */

/**
 * Module widths per symbol value, as digits: bar, space, bar, space, bar,
 * space. Values 0-102 are data, 103-105 are the start codes, 106 is stop.
 *
 * Straight from the Code 128 specification. Changing a digit here silently
 * produces labels that scan as the wrong item, so the table is verified against
 * known encodings in the tests rather than trusted.
 */
// Ten symbols per row, so a value can be counted to and checked against the
// printed specification table. One per line would be 107 lines nobody verifies.
// prettier-ignore
const PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];

const START_B = 104;
const STOP = 106;

/** Code 128 B encodes printable ASCII, space through `~`. */
export function isEncodableInCode128B(text: string): boolean {
  return (
    text.length > 0 &&
    [...text].every((character) => {
      const code = character.charCodeAt(0);
      return code >= 32 && code <= 126;
    })
  );
}

/**
 * Encode as alternating module widths, starting with a bar.
 *
 * `[2, 1, 1, 2, 1, 4, …]` means a 2-module bar, a 1-module space, and so on.
 * The renderer turns those into boxes; keeping the encoder free of any drawing
 * concern is what makes it testable against the specification.
 */
export function encodeCode128B(text: string): number[] {
  if (!isEncodableInCode128B(text)) {
    throw new RangeError(`encodeCode128B: cannot encode ${JSON.stringify(text)}`);
  }

  const values = [...text].map((character) => character.charCodeAt(0) - 32);

  // Weighted mod-103 check character: the start code counts once, then each
  // data value is weighted by its 1-based position.
  let checksum = START_B;
  values.forEach((value, index) => {
    checksum += value * (index + 1);
  });
  checksum %= 103;

  const symbols = [START_B, ...values, checksum, STOP];

  return symbols.flatMap((symbol) =>
    [...PATTERNS[symbol]!].map((width) => Number.parseInt(width, 10)),
  );
}

/** Total modules wide, for sizing the printed barcode to its label. */
export function code128Width(text: string): number {
  return encodeCode128B(text).reduce((total, width) => total + width, 0);
}

/** One drawn bar: where it starts and how wide it is, both in modules. */
export interface Code128Bar {
  readonly x: number;
  readonly width: number;
}

/**
 * The dark bars only, placed along the symbol.
 *
 * The widths alternate bar/space starting with a bar, so the renderer would
 * otherwise have to track that parity and a running offset itself — and getting
 * either wrong produces a barcode that looks right and scans as something else.
 * Doing it here means it is checked by the same tests as the encoding.
 */
export function code128Bars(text: string): { bars: Code128Bar[]; width: number } {
  const widths = encodeCode128B(text);
  const bars: Code128Bar[] = [];
  let x = 0;

  widths.forEach((width, index) => {
    if (index % 2 === 0) bars.push({ x, width });
    x += width;
  });

  return { bars, width: x };
}

/**
 * Quiet zone Code 128 requires either side of the symbol, in modules.
 *
 * **In modules, which is why the label's padding cannot supply it.** Padding is
 * a fixed number of inches and a module is however wide the code left room for,
 * so 0.1in of padding is ten modules on one label and five on the next. It came
 * to under five on the stock this shop bought, and a scanner that cannot find
 * the start of a symbol simply does not beep — which reads as a bad label, a bad
 * scanner or a bad item, and never as a layout bug.
 *
 * So the *renderer* draws it, inside the same viewBox as the bars, and the strip
 * then scales as one thing whatever width it is given. There is exactly one
 * renderer — `Barcode` in the POS, shared by the label and the receipt — which
 * is what makes putting it there safe.
 */
export const CODE128_QUIET_ZONE_MODULES = 10;

/**
 * The narrowest bar a thermal printer can reliably put down, in printer dots.
 *
 * One dot is the physical floor — below that the printer rounds bars to whole
 * dots and the ratios that carry the data are destroyed. Two is the working
 * minimum: on the 203 dpi printers this shop runs, two dots is 0.25 mm, which is
 * the X-dimension retail Code 128 is specified at.
 */
export const MIN_SCANNABLE_MODULE_DOTS = 2;

/**
 * How many printer dots wide each bar of this code would be, drawn across
 * `widthInches` at `dpi` — quiet zones included, because they are drawn too.
 *
 * **This is the number that decides whether a label scans**, and until it
 * existed nothing computed it. A label draws its barcode at the full width it
 * has, so the *strip* always fits — what shrinks is the module, silently, in
 * proportion to how long the code is. A 34-character SKU slug is 409 modules
 * against a six-digit number's 101, so across the same 2 inches the slug's bars
 * land under one dot and the number's at three and a half. One of those scans,
 * and nothing on screen tells the two apart.
 *
 * Returned rather than compared so the figure can be shown (§2.2-10): "0.97 dots
 * per bar" tells an operator what to do about it, and "unscannable" does not.
 */
export function code128ModuleDots(text: string, widthInches: number, dpi: number): number {
  if (!(widthInches > 0) || !(dpi > 0)) {
    throw new RangeError('code128ModuleDots: width and dpi must both be positive');
  }
  return (widthInches * dpi) / (code128Width(text) + 2 * CODE128_QUIET_ZONE_MODULES);
}
